import * as React from 'react';
import { useEffect, useState } from 'react';
import { Redirect } from 'react-router';
import {
  Button,
  CollectionPreferences,
  CollectionPreferencesProps,
  Flashbar,
  Modal,
  Pagination,
  PropertyFilter,
  Table,
  TableProps,
} from '@amzn/awsui-components-react-v3/polaris';
import { listDataSets } from 'src/api/catalog';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { editDataLakeRole, listDataLakeRoleProperty } from 'src/api/permissions';
import { defaultWrapLinesPreference, i18nStrings, largePageSizePreference, paginationLabels } from 'src/commons/tables';
import { compareBy } from 'src/components/utils/sorting';
import {
  DATA_LOAD_LIMIT,
  DZ_REDSHIFT_DATASOURCE_ID,
  IAM_ACCESS_ITEM,
  REDSHIFT_DATASOURCE_ID,
  TABLE_CONTENT_TYPE,
} from 'src/commons/constants';
import { createCatalogDetailLink, createDatasetDetailLink, createDatabaseDetailLink } from 'src/routes';
import { CopiableText, DefaultRouteProps, keyForCatalogNameMap } from 'src/commons/common';
import { ownerLinkItem } from 'src/components/workspaces/common/common';
import { EmptyState } from 'src/commons/EmptyState';
import { CopyToClipboard, Header, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { PageHeader } from 'src/components/common/PageHeader';
import { Link } from 'src/components/common/Link';
import { CartAccessItem } from 'src/components/common/checkout/common';

export interface BrowseTableProps extends DefaultRouteProps {
  allowListed: boolean;
  setContentType: any;
  idFilter?: string[];
  extraFeatures: boolean;
  onlyShowDatasetName?: boolean;
  title: string;
  loading?: boolean;
  roleArns?: string[];
  cartItemIds?: string[];
  addToCart?: (items: CartAccessItem[]) => void;
  activeGroup?: string;
  iamDatasetPermission?: boolean;
  multiSelection?: boolean;
  disableFiltering?: boolean;
  catalogMap?: any;
}

const findRoleArn = (item, roleArns) => {
  if (roleArns == null) return null;

  for (const roleArn of roleArns) {
    if (roleArn.split(':')[4] == item.IdInfo.CatalogId) {
      return roleArn;
    }
  }
};

export const flattenItem = (item, roleArns, catalog) => {
  return {
    dataSetName: item['DataSetName'],
    clusterId: item['IdInfo']['ClusterIdentifier'],
    redshiftWorkgroupName: item['IdInfo']['RedshiftWorkgroupName'],
    databaseName: item['IdInfo']['DatabaseName'],
    tableName: item['IdInfo']['TableName'],
    dataSourceId: item['IdInfo']['DataSourceId'],
    catalogId: item['IdInfo']['CatalogId'],
    region: item['IdInfo']['Region'],
    catalogInfo: item['catalogInfo'] || catalog,
    catalogDisplayName: item['catalogDisplayName'],
    id: item['Id'],
    roleArn: findRoleArn(item, roleArns),
    supportedAccessTypes: item['SupportedAccessTypes'],
    owners: item['Owners'],
    dataClassificationOption: item['DataClassification'] === 'Private' ? item['DataClassification'] : 'Public',
    primaryOwner: item['PrimaryOwner'],
  };
};

const BrowseTable = (props: BrowseTableProps) => {
  const [allItems, setItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [redirect] = useState<string>(undefined);
  const [isLoading, setLoading] = useState(true);
  const [roleProperties, setRoleProperties] = useState(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [paginationDisabled, setPaginationDisabled] = useState(false);
  const [nextToken, setNextToken] = useState<string>(null);
  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 25,
  });

  const [columnDefinitions, setColumnDefinitions] = useState<TableProps.ColumnDefinition<any>[]>([
    {
      id: 'tableName',
      header: 'Table name',
      cell: (item) => <Link href={createDatasetDetailLink(item?.id)}>{item.tableName}</Link>,
      minWidth: '200px',
      sortingField: props.extraFeatures || props.multiSelection ? 'tableName' : null,
    },
    {
      id: 'databaseName',
      header: 'Database name',
      cell: (item) => (
        <Link
          href={createDatabaseDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            undefined,
            undefined,
            item?.databaseName,
            item?.region,
          )}
        >
          {item.databaseName}
        </Link>
      ),
      minWidth: '200px',
      sortingField: props.extraFeatures || props.multiSelection ? 'databaseName' : null,
      sortingComparator: compareBy('databaseName', 'tableName'),
    },
    {
      id: 'catalogDisplayName',
      header: 'Catalog name',
      cell: (item) => (
        <Link
          href={createCatalogDetailLink(
            item?.dataSourceId,
            item?.catalogId,
            item?.clusterId,
            item?.redshiftWorkgroupName,
            item?.region,
          )}
        >
          {item.catalogDisplayName}
        </Link>
      ),
      minWidth: '100px',
      sortingField: props.extraFeatures || props.multiSelection ? 'catalogDisplayName' : null,
      sortingComparator: compareBy('catalogDisplayName', 'databaseName', 'tableName'),
    },
    {
      id: 'owner',
      header: 'Owner',
      cell: (item) => ownerLinkItem(item.primaryOwner, props.workspaceNameMap),
      minWidth: '200px',
      sortingField: 'owner',
    },
  ]);

  // Load role properties.  Currently used to indicate that some items
  // can't be added to cart because they are pending permissions or
  // have already approved permissions.  Note that the active group

  // simplifies the dataset objects in order to make the rest of the code a bit cleaner
  const flattenItems = (items, roleArns): CartAccessItem[] => {
    return items.map((item) => ({
      ...flattenItem(item, roleArns, null),
      catalogDisplayName: getCatalogName(item),
      catalogName: getCatalogName(item),
      // other required items for cart checkouts
      datasetName: item['DataSetName'],
      owner: item['PrimaryOwner'],
      itemType: IAM_ACCESS_ITEM,
      dataOwnerRole: undefined,
      dataLakePrincipal: undefined,
      permissionId: undefined,
    }));
  };

  const getCatalogName = (item: any) => {
    return (
      props.catalogMap.get(
        keyForCatalogNameMap(
          item.IdInfo?.DataSourceId,
          item.IdInfo?.CatalogId,
          item.IdInfo?.ClusterIdentifier,
          item.IdInfo?.RedshiftWorkgroupName,
          item.IdInfo?.Region,
        ),
      ) ?? item.IdInfo?.CatalogId
    );
  };

  // is an optional props
  const loadRoleProperties = async () => {
    const roleProperties = await listDataLakeRoleProperty({
      groupId: props.activeGroup,
    });

    setRoleProperties(roleProperties);
  };

  const componentDidMount = async () => {
    if (props.onlyShowDatasetName) {
      setColumnDefinitions([
        {
          id: 'dataSetName',
          header: 'Table name',
          cell: (item) => <Link href={createDatasetDetailLink(item?.id)}>{item.tableName}</Link>,
          minWidth: '200px',
        },
      ]);
    }

    props.setContentType(TABLE_CONTENT_TYPE);
    if (props.roleArns != null) {
      columnDefinitions.push({
        id: 'roleArn',
        header: 'Role ARN',
        cell: (item) => (
          <CopyToClipboard
            textToCopy={item.roleArn}
            textToDisplay={item.roleArn}
            copySuccessText={'Successfully copied role ARN'}
            copyErrorText={'Failed to copy role ARN'}
            variant={'inline'}
          />
        ),
        minWidth: '200px',
      });
    }

    setLoading(true);
    await fetchAllData();
    if (props.extraFeatures && props.activeGroup) await loadRoleProperties();
    setLoading(false);
  };

  useEffect(() => {
    componentDidMount();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [props.idFilter]);

  useEffect(() => {
    if (props.extraFeatures && props.activeGroup) loadRoleProperties();
  }, [props.activeGroup]);

  // Fetch datasets and then their catalogs
  const fetchAllData = async () => {
    setLoading(true);
    await fetchDatasets();
    setLoading(false);
  };

  const fetchDatasets = async () => {
    let dataSets;
    let dataSetList = [];
    try {
      if (props.idFilter) {
        for (let index = 0; index < props.idFilter.length; index += DATA_LOAD_LIMIT) {
          let request = {
            Filter: { IdList: props.idFilter.slice(index, index + DATA_LOAD_LIMIT) },
            Limit: DATA_LOAD_LIMIT,
            IncludeTotalCount: true,
          };
          dataSets = await listDataSets(request);
          let currentList = dataSets.DataSetList.filter(
            (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceType),
          );
          dataSetList.push(...currentList);
        }
        setNextToken(null);
        setAllDataLoaded(true);
        setTotalCount(props.idFilter.length);
      } else {
        let request = { Limit: DATA_LOAD_LIMIT, IncludeTotalCount: true };
        dataSets = await listDataSets(request);
        setNextToken(dataSets.NextToken);
        if (dataSets.NextToken == null) {
          setAllDataLoaded(true);
        }
        setTotalCount(dataSets.TotalCount);
        dataSetList = dataSets.DataSetList.filter(
          (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceType),
        );
      }
      // here, datasets are filtered by "public" and "restricted" only,
      // The "private" datasets will be visible only to owners
      let publicAndRestrictedDataSets = [];
      let privateDataSets = [];
      for (const dataSet of dataSetList) {
        if (dataSet.DataClassification === 'Public' || dataSet.DataClassification === 'Restricted') {
          publicAndRestrictedDataSets.push(dataSet);
        } else {
          if (dataSet.Owners.includes(props.activeGroup)) {
            privateDataSets.push(dataSet);
          }

          // Apply valid Primary owner to datasets without one so it can be filterable
          dataSet.PrimaryOwner = dataSet.PrimaryOwner != null ? dataSet.PrimaryOwner : 'No primary owner';
        }
      }
      const datasets = publicAndRestrictedDataSets.concat(privateDataSets);
      const flatItems = flattenItems(datasets, props.roleArns);
      setItems(flatItems);
    } catch (e) {
      console.log('Exception when fetch datasets', e);
      setNotifications([
        {
          type: 'error',
          content: `Error when trying to fetch datasets: ${e.message}`,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
  };

  const loadMoreData = async () => {
    if (props.idFilter) {
      return;
    }
    setPaginationDisabled(true);
    let request = { Limit: DATA_LOAD_LIMIT, NextToken: nextToken };
    let dataSets = await listDataSets(request);

    setNextToken(dataSets.NextToken);
    let dataSetList = dataSets.DataSetList.filter(
      (item) => ![REDSHIFT_DATASOURCE_ID, DZ_REDSHIFT_DATASOURCE_ID].includes(item.DataSourceType),
    );
    // here, datasets are filtered by "public" and "restricted" only,
    // The "private" datasets will be visible only to owners
    let publicAndRestrictedDataSets = [];
    let privateDataSets = [];
    for (const dataSet of dataSetList) {
      if (dataSet.DataClassification === 'Public' || dataSet.DataClassification === 'Restricted') {
        publicAndRestrictedDataSets.push(dataSet);
      } else {
        if (dataSet.Owners.includes(props.activeGroup)) {
          privateDataSets.push(dataSet);
        }
      }

      // Apply valid Primary owner to datasets without one so it can be filterable
      dataSet.PrimaryOwner = dataSet.PrimaryOwner != null ? dataSet.PrimaryOwner : 'No primary owner';
    }

    const datasets = publicAndRestrictedDataSets.concat(privateDataSets);

    const flatItems = flattenItems(datasets, props.roleArns);
    let currentItems = allItems;
    currentItems.push(...flatItems);
    setItems(currentItems);
    if (dataSets.NextToken == null) {
      setAllDataLoaded(true);
    }
    setPaginationDisabled(false);
  };

  const handleIamDatasetsPermissionActions = async (e) => {
    if (e.detail.id === 'relinquish') openModal();
  };

  const handleRelinquishPermission = async () => {
    setButtonLoading(true);

    const datasetsId = [];
    for (var i = 0; i < selected.length; i++) {
      datasetsId.push(selected[i].id);
    }

    await editDataLakeRole({
      groupId: props.activeGroup,
      datasetsToDelete: datasetsId,
    });

    setSelected([]);

    for (let i = 0; i < datasetsId.length; i++) {
      props.idFilter.splice(props.idFilter.indexOf(datasetsId[i]), 1);
    } // Potential bug: datasets are spliced on the front-end, regardless of the response.

    closeModal();

    setLoading(true);
    await fetchDatasets();
    setLoading(false);
  };

  const openModal = () => {
    setModalVisible(true);
  };

  const closeModal = () => {
    setButtonLoading(false);
    setModalVisible(false);
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(allItems, {
    filtering: {},
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'catalogDisplayName',
          sortingComparator: compareBy('catalogDisplayName', 'databaseName', 'tableName'),
        },
      },
    },
    selection: {},
    propertyFiltering: {
      noMatch: {}, // filtering is disabled
      empty: <EmptyState title='No datasets' subtitle='No datasets were found in the catalog.' />,
      filteringProperties: [
        {
          propertyLabel: 'Catalog name',
          key: 'catalogDisplayName',
          groupValuesLabel: 'Catalog names',
        },
        {
          propertyLabel: 'Database',
          key: 'databaseName',
          groupValuesLabel: 'Databases',
        },
        {
          propertyLabel: 'Table name',
          key: 'tableName',
          groupValuesLabel: 'Table names',
        },
        {
          propertyLabel: 'Data visibility',
          key: 'dataClassificationOption',
          groupValuesLabel: 'Data visibility options',
        },
        {
          propertyLabel: 'Primary owner',
          key: 'primaryOwner',
          groupValuesLabel: 'Primary owners',
        },
      ],
    },
  });

  const handlePageChange = async (e) => {
    if (paginationProps.currentPageIndex == paginationProps.pagesCount && !allDataLoaded) {
      await loadMoreData();
    }
    if (
      paginationProps.currentPageIndex != paginationProps.pagesCount ||
      (e.detail.currentPageIndex <= paginationProps.pagesCount &&
        e.detail.currentPageIndex != paginationProps.currentPageIndex)
    ) {
      paginationProps.onChange(e);
    }
  };

  if (redirect) return <Redirect push to={redirect} />;

  return (
    <>
      <Flashbar items={notifications} />
      <Table
        {...collectionProps}
        loadingText='Loading datasets...'
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        trackBy={'id'}
        selectedItems={selected}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
        header={
          <>
            <PageHeader
              buttons={
                props.extraFeatures && props.addToCart
                  ? [
                      {
                        text: 'Add to cart',
                        variant: 'normal',
                        disabled: selected.length === 0,
                        onItemClick: () => {
                          props.addToCart(selected);
                          setSelected([]);
                        },
                      },
                    ]
                  : props.iamDatasetPermission
                  ? [
                      {
                        text: 'Actions',
                        onItemClick: handleIamDatasetsPermissionActions,
                        items: [
                          {
                            text: 'Relinquish permission',
                            id: 'relinquish',
                            disabled: !selected.length,
                          },
                        ],
                        loading: isLoading,
                      },
                    ]
                  : []
              }
              counter={`(${totalCount}${!allDataLoaded && props.idFilter ? '+' : ''})`}
              header={props.title}
            />

            <Modal
              visible={modalVisible}
              header={[`Are you sure to relinquish the permission for datasets below?`]}
              onDismiss={closeModal}
              footer={
                <Header
                  actions={
                    <SpaceBetween size={'s'} direction='horizontal'>
                      <Button variant='link' onClick={closeModal}>
                        No
                      </Button>
                      <Button variant='primary' loading={buttonLoading} onClick={handleRelinquishPermission}>
                        Yes
                      </Button>
                    </SpaceBetween>
                  }
                />
              }
            >
              <div>
                Do you want to remove permissions for datasets below?
                {selected.map(({ id }) => (
                  <li key={id}>{id}</li>
                ))}
              </div>
            </Modal>
          </>
        }
        selectionType={(props.extraFeatures || props.multiSelection) && props.allowListed ? 'multi' : undefined}
        isItemDisabled={(item) => {
          if (!props.allowListed) return false;
          if (props.multiSelection) return;
          return (
            item['supportedAccessTypes'] == null ||
            // Currently disabling all IAM bulk permission requests
            !item['supportedAccessTypes'].includes('IAM') ||
            // Not allowed to re-add if already in cart
            props.cartItemIds.includes(item['id']) ||
            // Not allowed to add tables that are pending or already approved
            (roleProperties !== undefined &&
              (roleProperties.approvedPermissions.includes(item['id']) ||
                roleProperties.pendingPermissions.includes(item['id'])))
          );
        }}
        loading={isLoading}
        filter={
          (props.extraFeatures || props.multiSelection) && (
            <PropertyFilter
              {...propertyFilterProps}
              disabled={isLoading || props.disableFiltering}
              i18nStrings={
                props.disableFiltering
                  ? {
                      ...i18nStrings,
                      filteringPlaceholder: 'To discover datasets, use search page instead.',
                    }
                  : i18nStrings
              }
              countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
            />
          )
        }
        pagination={
          (props.extraFeatures || props.multiSelection) && (
            <Pagination
              {...paginationProps}
              ariaLabels={paginationLabels}
              onChange={(e) => handlePageChange(e)}
              // currentPageIndex={currentPageIndex}
              openEnd={!allDataLoaded}
              disabled={paginationDisabled}
            />
          )
        }
        preferences={
          (props.extraFeatures || props.multiSelection) && (
            <CollectionPreferences
              title={'Preferences'}
              confirmLabel={'Confirm'}
              cancelLabel={'Cancel'}
              preferences={preferences}
              onConfirm={({ detail }) => setPreferences(detail)}
              pageSizePreference={largePageSizePreference}
              wrapLinesPreference={defaultWrapLinesPreference}
            />
          )
        }
      />
    </>
  );
};

export default BrowseTable;
